/**
 * =============================================================================
 * SCRIPT-NAME:   seiten_pruefung.js
 * PROJEKT:       Freiseinapp - GSC
 * MODUL:         Taegliche Seitenpruefung und Lebenslauf je Seite und Sitemap
 * BESCHREIBUNG:  Liest gsc_seiten_pruefung_api.php. Oben die Laeufe mit Ampel, darunter
 *                die Befunde des gewaehlten Laufs, dann je Seite und je Sitemap eine Zeile.
 *                Ein Klick auf die Zeile oeffnet den Lebenslauf: jeder Tageszustand und
 *                jeder Upload mit Datum, Uhrzeit und Version.
 * ANLASS:        Georg 2026-11-02: "wir brauchen einen taeglichen Eintrag fuer jede Seite" ·
 *                "die Sitemap wie eine eigene Seite" · "wann wir eine Seite neu hochgeladen
 *                haben, mit Datum Uhrzeit und Version".
 * VERSION:       v1.0 · STAND: 2026-11-02 Europe/Berlin
 * =============================================================================
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import "./seiten_pruefung.css";

const API = "https://freiseinapp.de/api/gsc_seiten_pruefung_api.php";
const PRUEF = { S1: 'lastmod = WordPress', S2: 'Sitemap vollständig', S3: 'HTTP 200', S4: 'Google holt ab', S5: 'im Index' };

const pfad = u => String(u || '').replace('https://verlustangst24.de', '') || '/';
const zeit = t => t ? String(t).replace('T', ' ').slice(0, 16) : '–';

function Ampel({ wert }) {
    if (!wert || wert === '-') return <span className="sp-a sp-a-leer">–</span>;
    const k = wert === 'ok' ? 'gruen' : wert === 'rot' ? 'rot' : 'gelb';
    return <span className={`sp-a sp-a-${k}`} title={wert}>{wert === 'ok' ? '✓' : wert === 'rot' ? '✗' : '?'}</span>;
}

function StandPill({ stand }) {
    const k = stand === 'GRUEN' ? 'gruen' : stand === 'ROT' ? 'rot' : 'gelb';
    return <span className={`sp-pill sp-pill-${k}`}>{stand === 'GRUEN' ? 'grün' : stand === 'ROT' ? 'rot' : 'ungeprüft'}</span>;
}

function Lebenslauf({ url, token }) {
    const [d, setD] = useState(null);
    useEffect(() => {
        let alive = true;
        fetch(`${API}?aktion=lebenslauf&url=${encodeURIComponent(url)}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(j => { if (alive) setD(j); }).catch(() => {});
        return () => { alive = false; };
    }, [url, token]);
    if (!d) return <div className="sp-ll">lädt …</div>;
    const sitemap = (d.tage[0] || {}).art === 'sitemap';
    return (
        <div className="sp-ll">
            {!sitemap && <>
                <h4>Uploads <small>({d.uploads.length})</small></h4>
                {d.uploads.length === 0 ? <p className="sp-leer">noch kein Upload erfasst</p> :
                <table className="sp-t sp-t-klein"><thead><tr>
                    <th>Version</th><th>Stempel</th><th>erfasst</th><th>Quelle</th><th>Sitemap</th>
                </tr></thead><tbody>{d.uploads.map(u => (
                    <tr key={u.id}><td>v{u.version}</td><td>{u.stempel_zeit}</td><td>{zeit(u.erfasst_am)}</td>
                        <td>{u.quelle}</td><td>{u.sitemap_ok === null ? '–' : <Ampel wert={Number(u.sitemap_ok) ? 'ok' : 'rot'} />}</td></tr>
                ))}</tbody></table>}
            </>}
            <h4>Tageszustände <small>({d.tage.length})</small></h4>
            <table className="sp-t sp-t-klein"><thead><tr>
                <th>Lauf</th><th>HTTP</th>
                {sitemap ? <><th>URLs</th><th>lastmod im Index</th><th>Google abgeholt</th><th>S3</th><th>S4</th></>
                         : <><th>Sitemap</th><th>lastmod</th><th>WordPress</th><th>Stempel</th><th>S1</th><th>S2</th><th>S3</th></>}
            </tr></thead><tbody>{d.tage.map(t => (
                <tr key={t.lauf_id}><td>{zeit(t.lauf_am)}</td><td>{t.http_code ?? '–'}</td>
                    {sitemap ? <><td>{t.sm_urls ?? '–'}</td><td>{zeit(t.lastmod)}</td><td>{zeit(t.google_abgeholt)}</td>
                                 <td><Ampel wert={t.s3} /></td><td><Ampel wert={t.s4} /></td></>
                             : <><td>{Number(t.in_sitemap) ? 'ja' : 'nein'}</td><td>{zeit(t.lastmod)}</td><td>{zeit(t.wp_modified_gmt)}</td>
                                 <td>{t.version ? `v${t.version} · ${t.stempel_zeit}` : '–'}</td>
                                 <td><Ampel wert={t.s1} /></td><td><Ampel wert={t.s2} /></td><td><Ampel wert={t.s3} /></td></>}
                </tr>
            ))}</tbody></table>
        </div>
    );
}

export default function SeitenPruefung() {
    const token = localStorage.getItem("token");
    const [laeufe, setLaeufe] = useState([]);
    const [lauf, setLauf] = useState(null);
    const [seiten, setSeiten] = useState([]);
    const [befunde, setBefunde] = useState([]);
    const [offen, setOffen] = useState(null);
    const [suche, setSuche] = useState('');
    const [nurAuffaellig, setNurAuffaellig] = useState(false);

    useEffect(() => {
        fetch(`${API}?aktion=laeufe`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(j => { setLaeufe(j.laeufe || []); if (j.laeufe?.length) setLauf(j.laeufe[0].id); })
            .catch(() => {});
    }, [token]);

    useEffect(() => {
        if (!lauf) return;
        const h = { headers: { Authorization: `Bearer ${token}` } };
        fetch(`${API}?aktion=seiten&lauf=${lauf}`, h).then(r => r.json()).then(j => setSeiten(j.seiten || [])).catch(() => {});
        fetch(`${API}?aktion=befunde&lauf=${lauf}`, h).then(r => r.json()).then(j => setBefunde(j.befunde || [])).catch(() => {});
    }, [lauf, token]);

    const zeigen = useMemo(() => {
        const q = suche.trim().toLowerCase();
        return seiten.filter(s => !q || s.url.toLowerCase().includes(q) || (s.kuerzel || '').includes(q))
            .filter(s => !nurAuffaellig || [s.s1, s.s2, s.s3, s.s4].some(v => v && v !== 'ok' && v !== '-'));
    }, [seiten, suche, nurAuffaellig]);

    const aktuell = laeufe.find(l => l.id === lauf);
    const nSitemap = seiten.filter(s => s.art === 'sitemap').length;

    return (
        <div className="sp-wrap">
            <Link to="/admin" className="sp-back">← Admin</Link>
            <h2>Seitenprüfung <small>täglich 01:00 · S1 bis S5 · Lebenslauf je Seite und Sitemap</small></h2>

            <div className="sp-laeufe">
                {laeufe.length === 0 && <span className="sp-leer">noch kein Lauf</span>}
                {laeufe.map(l => (
                    <button key={l.id} className={`sp-lauf ${l.id === lauf ? 'aktiv' : ''}`} onClick={() => { setLauf(l.id); setOffen(null); }}>
                        <span>{zeit(l.lauf_am)}</span><StandPill stand={l.stand} />
                        {(l.rot > 0 || l.ungeprueft > 0) && <small>{l.rot} rot · {l.ungeprueft} ungeprüft</small>}
                    </button>
                ))}
            </div>

            {aktuell && <p className="sp-zus">{aktuell.zusammenfassung} · Dauer {aktuell.dauer_s} s · Mail: {aktuell.mail || '–'}</p>}

            <h3>Befunde <small>({befunde.length})</small></h3>
            {befunde.length === 0 ? <p className="sp-leer">keine - alles grün</p> :
            <div className="sp-tw"><table className="sp-t"><thead><tr><th>Prüfung</th><th>Art</th><th>URL</th><th>Befund</th></tr></thead>
                <tbody>{befunde.map((b, i) => (
                    <tr key={i} className={b.art === 'rot' ? 'sp-z-rot' : 'sp-z-gelb'}>
                        <td title={PRUEF[b.pruefung]}>{b.pruefung}</td><td>{b.art}</td><td>{pfad(b.url)}</td><td className="sp-wrap-txt">{b.befund}</td>
                    </tr>))}</tbody></table></div>}

            <h3>Seiten und Sitemaps <small>({seiten.length - nSitemap} Seiten · {nSitemap} Sitemaps)</small></h3>
            <div className="sp-ctrl">
                <input placeholder="Seite suchen (Pfad oder lb06)" value={suche} onChange={e => setSuche(e.target.value)} />
                <label><input type="checkbox" checked={nurAuffaellig} onChange={e => setNurAuffaellig(e.target.checked)} /> nur auffällige</label>
                <span className="sp-leg">S1 {PRUEF.S1} · S2 {PRUEF.S2} · S3 {PRUEF.S3} · S4 {PRUEF.S4}</span>
            </div>
            <div className="sp-tw">
            <table className="sp-t"><thead><tr>
                <th>Seite</th><th>Stempel</th><th>letzter Upload / Google</th><th>HTTP</th><th>Sitemap</th><th>lastmod</th>
                <th>S1</th><th>S2</th><th>S3</th><th>S4</th>
            </tr></thead><tbody>{zeigen.map(s => (
                <React.Fragment key={s.url}>
                    <tr className={`sp-zeile ${s.art === 'sitemap' ? 'sp-z-sm' : ''} ${offen === s.url ? 'offen' : ''}`}
                        onClick={() => setOffen(offen === s.url ? null : s.url)}>
                        <td>{s.art === 'sitemap' ? '🗺 ' : ''}{pfad(s.url)}</td>
                        <td>{s.version ? <>{s.kuerzel} v{s.version}<br /><small>{s.stempel_zeit}</small></>
                                       : (s.art === 'sitemap' ? `${s.sm_urls ?? '–'} URLs` : '–')}</td>
                        <td>{s.upload_erfasst ? <>{zeit(s.upload_erfasst)}<br /><small>{s.upload_quelle} · {s.uploads}×</small></>
                                              : (s.art === 'sitemap' ? <>{zeit(s.google_abgeholt)}<br /><small>von Google abgeholt</small></> : '–')}</td>
                        <td>{s.http_code ?? '–'}</td>
                        <td>{s.art === 'sitemap' ? '–' : (Number(s.in_sitemap) ? 'ja' : 'nein')}</td>
                        <td>{zeit(s.lastmod)}</td>
                        <td><Ampel wert={s.s1} /></td><td><Ampel wert={s.s2} /></td><td><Ampel wert={s.s3} /></td><td><Ampel wert={s.s4} /></td>
                    </tr>
                    {offen === s.url && <tr className="sp-ll-zeile"><td colSpan={10}><Lebenslauf url={s.url} token={token} /></td></tr>}
                </React.Fragment>
            ))}</tbody></table>
            </div>
        </div>
    );
}
